import { request } from './base.service';

// ── Existing types (kept for API compatibility) ──

export interface TemplateEntry {
    title: string;
    amount: number;
    type: string;
    tag?: string;
    category?: string;
    onCredit?: boolean;
}

export interface EntryTemplate {
    id: string;
    userId: string;
    name: string;
    entries: TemplateEntry[];
    createdAt: string;
    updatedAt: string;
}

// ── Rich Template types (for TemplatesPage UI) ──

export interface TplRecurrence {
    kind: 'monthly' | 'biweekly' | 'weekly' | 'custom' | 'once';
    anchor?: number | string;
}

export interface TplEntry {
    title: string;
    amount: number;
    day: number;
    cat: string;
}

export interface Template {
    id: string;
    name: string;
    color: string;
    icon: string;
    recurrence: TplRecurrence;
    nextApply: string | null;
    lastApplied: string | null;
    autoApply: boolean;
    account: string;
    tags: string[];
    notes: string;
    appliedCount: number;
    status: 'active' | 'paused' | 'archived';
    entries: TplEntry[];
}

// ── Design tokens ──

export const TPL_PURPLE = '#7C3AED';
export const TPL_PURPLE_DARK = '#6D28D9';
export const TPL_PURPLE_SOFT = '#EDE9FE';
export const TPL_PURPLE_PALE = '#F5F3FF';
export const TPL_INK = '#0F172A';
export const TPL_INK_700 = '#334155';
export const TPL_SLATE = '#64748B';
export const TPL_SLATE_LIGHT = '#94A3B8';
export const TPL_HAIR = '#E2E8F0';
export const TPL_HAIR_SOFT = '#EEF2F6';
export const TPL_BG = '#F8FAFC';
export const TPL_RED = '#EF4444';
export const TPL_GREEN = '#10B981';
export const TPL_AMBER = '#F59E0B';
export const TPL_BLUE = '#2563EB';

// ── Category visuals ──

export interface CatMeta {
    bg: string;
    fg: string;
    icon: string;
    label: string;
}

export const TPL_CAT: Record<string, CatMeta> = {
    housing:       { bg: '#EDE9FE', fg: '#7C3AED', icon: 'home',           label: 'Housing' },
    utilities:     { bg: '#E5E7EB', fg: '#525252', icon: 'bolt',           label: 'Utilities' },
    transport:     { bg: '#DBEAFE', fg: '#2563EB', icon: 'directions_car', label: 'Transport' },
    entertainment: { bg: '#FCE7F3', fg: '#DB2777', icon: 'sports_esports', label: 'Entertainment' },
    health:        { bg: '#D1FAE5', fg: '#059669', icon: 'favorite',       label: 'Health' },
    food:          { bg: '#FED7AA', fg: '#EA580C', icon: 'restaurant',     label: 'Food' },
    education:     { bg: '#CFFAFE', fg: '#0891B2', icon: 'school',         label: 'Education' },
    shopping:      { bg: '#FEE2E2', fg: '#DC2626', icon: 'shopping_bag',   label: 'Shopping' },
    other:         { bg: '#F1F5F9', fg: '#475569', icon: 'category',       label: 'Other' },
    income:        { bg: '#D1FAE5', fg: '#16A34A', icon: 'trending_up',    label: 'Income' },
};

// ── Color swatches ──

export interface ColorSwatch {
    id: string;
    name: string;
    bg: string;
    fg: string;
}

export const TPL_COLORS: ColorSwatch[] = [
    { id: 'purple', name: 'Purple', bg: '#EDE9FE', fg: '#7C3AED' },
    { id: 'blue',   name: 'Blue',   bg: '#DBEAFE', fg: '#2563EB' },
    { id: 'teal',   name: 'Teal',   bg: '#CCFBF1', fg: '#0D9488' },
    { id: 'green',  name: 'Green',  bg: '#D1FAE5', fg: '#059669' },
    { id: 'amber',  name: 'Amber',  bg: '#FEF3C7', fg: '#D97706' },
    { id: 'orange', name: 'Orange', bg: '#FED7AA', fg: '#EA580C' },
    { id: 'pink',   name: 'Pink',   bg: '#FCE7F3', fg: '#DB2777' },
    { id: 'slate',  name: 'Slate',  bg: '#E2E8F0', fg: '#475569' },
];

export const TPL_COLOR_BY_ID: Record<string, ColorSwatch> = Object.fromEntries(
    TPL_COLORS.map(c => [c.id, c])
);

// ── Mock data ──

export const MOCK_TEMPLATES: Template[] = [
    {
        id: 't1',
        name: 'Monthly bills',
        color: 'purple',
        icon: 'home',
        recurrence: { kind: 'monthly', anchor: 1 },
        nextApply: '2026-06-01',
        lastApplied: '2026-05-01',
        autoApply: true,
        account: 'Personal — Main',
        tags: ['fixed', 'essentials'],
        notes: 'Core monthly obligations. Refresh amounts in November after annual renewals.',
        appliedCount: 7,
        status: 'active',
        entries: [
            { title: 'Rent',               amount: -1800,  day: 15, cat: 'housing' },
            { title: 'Hydro-Québec',        amount: -150,   day: 25, cat: 'utilities' },
            { title: 'Vidéotron Internet',  amount: -78.21, day: 30, cat: 'utilities' },
            { title: 'Car Insurance',       amount: -150,   day: 28, cat: 'transport' },
            { title: 'Spotify',             amount: -10.99, day:  3, cat: 'entertainment' },
            { title: 'Daycare',             amount: -350,   day: 22, cat: 'health' },
        ],
    },
    {
        id: 't2',
        name: 'Bi-weekly paycheck',
        color: 'green',
        icon: 'trending_up',
        recurrence: { kind: 'biweekly', anchor: '2026-05-08' },
        nextApply: '2026-05-22',
        lastApplied: '2026-05-08',
        autoApply: true,
        account: 'Personal — Main',
        tags: ['income'],
        notes: '',
        appliedCount: 14,
        status: 'active',
        entries: [
            { title: 'Salary — Acme Corp', amount:  2480, day: 0, cat: 'income' },
            { title: 'RRSP contribution',  amount:  -240, day: 0, cat: 'other' },
        ],
    },
    {
        id: 't3',
        name: 'Weekly groceries',
        color: 'orange',
        icon: 'restaurant',
        recurrence: { kind: 'weekly', anchor: 'fri' },
        nextApply: '2026-05-08',
        lastApplied: '2026-05-01',
        autoApply: false,
        account: 'Personal — Main',
        tags: ['variable'],
        notes: 'Estimates only — actual amounts vary.',
        appliedCount: 21,
        status: 'active',
        entries: [
            { title: 'Whole Foods', amount: -110, day: 5, cat: 'food' },
            { title: 'Metro',       amount:  -85, day: 0, cat: 'food' },
        ],
    },
    {
        id: 't4',
        name: 'Daycare + lessons',
        color: 'teal',
        icon: 'favorite',
        recurrence: { kind: 'monthly', anchor: 1 },
        nextApply: '2026-06-01',
        lastApplied: '2026-05-01',
        autoApply: false,
        account: 'Joint — Family',
        tags: ['kids'],
        notes: 'Pause in July & August (summer camp instead).',
        appliedCount: 4,
        status: 'active',
        entries: [
            { title: 'Daycare',      amount: -1280, day:  1, cat: 'health' },
            { title: 'Swim lessons', amount:  -180, day: 10, cat: 'health' },
            { title: 'Piano',        amount:  -240, day: 15, cat: 'education' },
        ],
    },
    {
        id: 't5',
        name: 'Vacation — Costa Rica',
        color: 'amber',
        icon: 'flight',
        recurrence: { kind: 'once' },
        nextApply: null,
        lastApplied: '2025-12-18',
        autoApply: false,
        account: 'Joint — Family',
        tags: ['travel', 'one-off'],
        notes: 'Reusable for future trips — duplicate and edit dates.',
        appliedCount: 1,
        status: 'paused',
        entries: [
            { title: 'Flights',    amount: -2400, day:  1, cat: 'transport' },
            { title: 'Lodging',    amount: -1800, day:  5, cat: 'housing' },
            { title: 'Activities', amount:  -600, day: 10, cat: 'entertainment' },
            { title: 'Food',       amount:  -500, day:  3, cat: 'food' },
        ],
    },
    {
        id: 't6',
        name: 'Gym + subscriptions',
        color: 'pink',
        icon: 'sports_esports',
        recurrence: { kind: 'monthly', anchor: 5 },
        nextApply: '2026-06-05',
        lastApplied: '2026-05-05',
        autoApply: true,
        account: 'Personal — Main',
        tags: ['discretionary'],
        notes: '',
        appliedCount: 9,
        status: 'active',
        entries: [
            { title: 'Gym membership', amount:  -65,    day:  5, cat: 'health' },
            { title: 'Netflix',        amount:  -19.99, day:  8, cat: 'entertainment' },
            { title: 'iCloud',         amount:   -3.99, day: 12, cat: 'utilities' },
            { title: 'NYT',            amount:   -4,    day: 15, cat: 'education' },
        ],
    },
];

// ── Helper functions ──

export const tplFmt = (n: number | null | undefined): string => {
    if (n == null) return '';
    const abs = Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (n < 0 ? '\u2212$' : '+$') + abs;
};

export const tplFmtPlain = (n: number | null | undefined): string => {
    if (n == null) return '$0.00';
    const abs = Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + abs;
};

export const tplExpenseTotal = (t: Template): number =>
    t.entries.filter(e => e.amount < 0).reduce((s, e) => s + e.amount, 0);

export const tplIncomeTotal = (t: Template): number =>
    t.entries.filter(e => e.amount > 0).reduce((s, e) => s + e.amount, 0);

export const tplRecurrenceLabel = (r: TplRecurrence | null | undefined): string => {
    if (!r) return '';
    if (r.kind === 'monthly') return `Monthly \u00b7 day ${r.anchor}`;
    if (r.kind === 'biweekly') return 'Every 2 weeks';
    if (r.kind === 'weekly') {
        const map: Record<string, string> = {
            mon: 'Mondays', tue: 'Tuesdays', wed: 'Wednesdays',
            thu: 'Thursdays', fri: 'Fridays', sat: 'Saturdays', sun: 'Sundays',
        };
        const anchor = String(r.anchor || '');
        return `Weekly \u00b7 ${map[anchor] || anchor}`;
    }
    if (r.kind === 'custom') return 'Custom';
    if (r.kind === 'once') return 'One-off';
    return r.kind;
};

export const tplRecurrenceShort = (r: TplRecurrence | null | undefined): string => {
    if (!r) return '';
    if (r.kind === 'monthly') return 'Monthly';
    if (r.kind === 'biweekly') return 'Bi-weekly';
    if (r.kind === 'weekly') return 'Weekly';
    if (r.kind === 'custom') return 'Custom';
    if (r.kind === 'once') return 'One-off';
    return r.kind;
};

export const tplFormatDate = (iso: string | null | undefined): string => {
    if (!iso) return '\u2014';
    const parts = iso.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parts[1] - 1]} ${parts[2]}, ${parts[0]}`;
};

export const tplFormatDateShort = (iso: string | null | undefined): string => {
    if (!iso) return '\u2014';
    const parts = iso.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parts[1] - 1]} ${parts[2]}`;
};

export interface CategoryBucket {
    cat: string;
    total: number;
    count: number;
}

export const tplByCategory = (t: Template): CategoryBucket[] => {
    const map: Record<string, CategoryBucket> = {};
    for (const e of t.entries) {
        if (!map[e.cat]) map[e.cat] = { cat: e.cat, total: 0, count: 0 };
        map[e.cat].total += e.amount || 0;
        map[e.cat].count += 1;
    }
    return Object.values(map).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
};

// ── Templates CRUD ──

export const getTemplates = (userid: string): Promise<{ templates: EntryTemplate[] }> =>
    request(`/entries/templates?userid=${encodeURIComponent(userid)}`);

export const createTemplate = (
    userid: string,
    name: string,
    entries: any[],
): Promise<{ template: EntryTemplate }> =>
    request('/entries/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, name, entries }),
    });

export const updateTemplate = (
    id: string,
    updates: { name?: string; entries?: any[] },
): Promise<{ template: EntryTemplate }> =>
    request(`/entries/templates/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
    });

export const deleteTemplate = (id: string): Promise<{ deleted: boolean }> =>
    request(`/entries/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ── Apply / Reapply ──

export const applyTemplate = (
    id: string,
    userid: string,
    listid: string,
    interval: 'weekly' | 'monthly',
    count: number,
    startFrom?: string,
): Promise<{ created: number; entries: any[] }> =>
    request(`/entries/templates/${encodeURIComponent(id)}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, listid, interval, count, ...(startFrom ? { startFrom } : {}) }),
    });

export const reapplyTemplate = (
    id: string,
    userid: string,
    listid: string,
): Promise<{ updated: number; message?: string }> =>
    request(`/entries/templates/${encodeURIComponent(id)}/reapply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, listid }),
    });

// ── Apply preview helpers ──

export interface ApplyPreviewRow {
    id: string;
    kind: 'add' | 'conflict' | 'match';
    template: TplEntry;
    existing: { id: string; title: string; amount: number; day: number; cat: string } | null;
    day: number;
    dateLabel: string;
}

export interface ApplyPreview {
    template: Template;
    targetIso: string;
    monthLabel: string;
    rows: ApplyPreviewRow[];
}

// Simulated existing entries for conflict preview (mock data)
export const TPL_TARGET_MONTH_ENTRIES: Array<{ id: string; title: string; amount: number; day: number; cat: string }> = [
    { id: 'e-may-1', title: 'Rent',          amount: -1800,   day: 15, cat: 'housing' },
    { id: 'e-may-2', title: 'Hydro-Québec',  amount: -156.40, day: 25, cat: 'utilities' },
    { id: 'e-may-3', title: 'Spotify',       amount:  -11.99, day:  3, cat: 'entertainment' },
    { id: 'e-may-4', title: 'Whole Foods',   amount:  -97.20, day:  9, cat: 'food' },
    { id: 'e-may-5', title: 'Coffee',        amount:   -6.50, day: 14, cat: 'food' },
];

export const tplBuildApplyPreview = (
    template: Template,
    targetIso: string,
    existing: Array<{ id: string; title: string; amount: number; day: number; cat: string }> = TPL_TARGET_MONTH_ENTRIES,
): ApplyPreview => {
    const parts = targetIso.split('-').map(Number);
    const y = parts[0];
    const m = parts[1];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = `${months[m - 1]} ${y}`;
    const rows: ApplyPreviewRow[] = template.entries.map((e, i) => {
        const exist = existing.find(
            x => x.title.toLowerCase() === e.title.toLowerCase() && x.day === e.day
        );
        let kind: 'add' | 'conflict' | 'match' = 'add';
        let existingEntry: ApplyPreviewRow['existing'] = null;
        if (exist) {
            const same = Math.abs((exist.amount || 0) - (e.amount || 0)) < 0.01;
            kind = same ? 'match' : 'conflict';
            existingEntry = exist;
        }
        return {
            id: `${template.id}-${i}`,
            kind,
            template: e,
            existing: existingEntry,
            day: e.day,
            dateLabel: `${months[m - 1]} ${String(e.day).padStart(2, '0')}`,
        };
    });
    return { template, targetIso, monthLabel, rows };
};

export const tplDefaultDecision = (row: ApplyPreviewRow): string => {
    if (row.kind === 'add') return 'add';
    if (row.kind === 'match') return 'skip';
    return 'skip'; // conflict default = skip
};

// ── Duplicate entries ──

export const duplicateEntries = (
    userid: string,
    listid: string,
    entryIds: string[],
    interval: 'weekly' | 'monthly',
    count: number,
    startFrom?: string,
): Promise<{ created: number }> =>
    request('/entries/duplicate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, listid, entryIds, interval, count, ...(startFrom ? { startFrom } : {}) }),
    });
